import { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import './FallingRewardItem.css';
import { getLotteryStatus, claimLotteryReward } from './lotteryService';
import RewardResultDialog from './RewardResultDialog';

const LOTUS_PHOTO_SRC = '/image/fall/lotus-photo-cutout.png';
const BODHI_PHOTO_SRC = '/image/fall/pu-ti-leaf.png';

function randomBetween(min, max) {
    return Math.random() * (max - min) + min;
}

// 參考 canvas 版飄落邏輯：每片花瓣有固定 angle（15~45°）決定飄速 v = 20/angle，
// 方向 dir（±1），以及唯一的 seed 讓 sin 相位錯開，呈現自然搖曳感
function makeFallState() {
    const vw         = window.innerWidth;
    const vh         = window.innerHeight;
    // 固定用手機版寬度（390px）做水平範圍，桌機螢幕也只在中央 390px 區域內隨機
    const MOBILE_W   = Math.min(vw, 390);
    const rangeWidth = MOBILE_W * 0.9;
    const rangeStart = (vw - rangeWidth) / 2;                  // 水平置中起點（px）
    const angle      = 15 + Math.random() * 30;                // 15 ~ 45 deg
    const dir        = Math.random() > 0.5 ? 1 : -1;
    const seed       = 1 + Math.random() * 100;                // 唯一相位種子（rAF 旋轉用）
    const scale      = randomBetween(0.6, 1.0);
    // 水平搖曳：振幅 20~55px、週期 3~7 秒的鐘擺式漂移
    const swayAmp    = randomBetween(20, 100);                  // px
    const swayPeriod = randomBetween(3000, 7000);              // ms
    const swayFreq   = (2 * Math.PI) / swayPeriod;            // rad/ms
    const swayPhase  = Math.random() * 2 * Math.PI;           // 隨機初始相位，不同步
    return {
        x:          rangeStart + randomBetween(0, rangeWidth), // 只在手機寬範圍內隨機
        y:          randomBetween(-vh, 0),                     // 隨機起點（螢幕外頂部）
        v:          (20 / angle) * 0.9,                        // 角度愈大速度愈慢
        angle,
        dir,
        seed,
        scale,
        swayAmp,
        swayFreq,
        swayPhase,
        rangeStart,                                            // 供 reset 時重新隨機 x 用
        rangeWidth,
        vw,
        vh,
    };
}

// next_time 是絕對時間字串（如 "Tue, 01 Sep 2026 06:13:33 GMT"），換算成距現在的毫秒數；
// 已過期或解析失敗則回傳 0（代表立即可以再檢查一次）
function msUntil(nextTime) {
    if (!nextTime) return 0;
    const target = new Date(nextTime).getTime();
    if (Number.isNaN(target)) return 0;
    return Math.max(0, target - Date.now());
}

/**
 * 全站可掛載的「機率飄落＋可點擊加分」彩蛋特效
 * A lottery-gated falling collectible that awards points on click
 *
 * 視覺與飄動方式跟 FallingWindPhoto 同一套即時風力模擬（寫實照片素材、requestAnimationFrame
 * 逐幀計算位置、中間範圍內連續彈性游走、2D 平面旋轉），差別在於這裡是單株可點擊、
 * 且飄落與否、加分數值都由後端 API 決定：
 *
 * - 掛載時呼叫 /v1/point/lottery/status；回傳 key 不為 null 時才開始飄落一片蓮花／
 *   菩提葉（未指定 type 時隨機二選一）
 * - 若目前是冷卻中（key 為 null），依 next_time 排程，時間到了自動再檢查一次
 * - 只要 key 還沒被成功 claim，同一組 key/lottery_id 會持續有效：這段期間花朵會在
 *   畫面中間範圍內持續飄動（彈性游走，飄出底部自動回到頂部重新飄），不會因為
 *   「這一輪沒點到」就消失——因為 key 沒變、狀態也沒變，重新查詢也只會拿到同一組 key
 * - 容器本身 pointer-events: none，只有花瓣/葉子本體 pointer-events: auto，
 *   不會擋住頁面上其他元素的點擊/操作
 * - 點擊後呼叫 claim（帶 status 給的 key/lottery_id）；成功則花瓣消失、原地彈出
 *   「+實際點數」飄浮文字，並依回應的新 next_time 進入冷卻排程；失敗（例如 key 已被
 *   使用）則提示錯誤並重新呼叫 status 取得目前實際狀態
 */
const FallingRewardItem = ({
    type,
    size = 90,
    spawnRangeRatio = 0.55,
    fallSpeed = 1.2,
    windMagnitude = 1.1,
    className = '',
}) => {
    const [phase, setPhase] = useState('idle'); // idle | falling | collected
    // null 代表 claim 回應還沒回來，此時不顯示 Dialog，避免使用者先看到 +0
    const [earnedPoints, setEarnedPoints] = useState(null);
    const containerRef = useRef(null);
    const hitRef = useRef(null);
    const pollTimerRef = useRef(null);
    const mountedRef = useRef(true);
    const rewardRef = useRef(null); // { lotteryId, key }
    const nextTimeRef = useRef(null);
    // rAF handle & mutable fall-state (x, y, angle, v, dir, seed, scale)
    const rafRef      = useRef(null);
    const fallState   = useRef(null);
    // live transform/opacity for the img element (updated each rAF frame)
    const [leafStyle, setLeafStyle] = useState({ opacity: 0, transform: 'translate3d(0px,0px,0) scale(1) rotate(0deg)' });

    // 未指定 type 時，每次觸發飄落時蓮花／菩提葉各半機率隨機決定
    const [resolvedType, setResolvedType] = useState(type ?? null);
    const imageSrc = resolvedType === 'bodhi' ? BODHI_PHOTO_SRC : LOTUS_PHOTO_SRC;
    const spin = resolvedType === 'bodhi';
    const maxSwayAngle = 15;

    const checkStatus = useCallback(async () => {
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        try {
            const res = await getLotteryStatus();
            if (!mountedRef.current) return;

            if (res.key) {
                rewardRef.current = { lotteryId: res.lottery_id, key: res.key };
                setResolvedType(type ?? (Math.random() > 0.5 ? 'lotus' : 'bodhi'));
                setPhase('falling');
            } else {
                // 冷卻中：依 next_time 排程下一次檢查
                pollTimerRef.current = setTimeout(checkStatus, msUntil(res.next_time) || 30000);
            }
        } catch (error) {
            console.error('Failed to get lottery status:', error);
            // API 失敗時退避重試，避免無限快速重打
            pollTimerRef.current = setTimeout(checkStatus, 60000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type]);

    useEffect(() => {
        mountedRef.current = true;
        checkStatus();
        return () => {
            mountedRef.current = false;
            if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 動畫核心：canvas 參考版 rAF 逐幀物理
    //   x / y 逐幀累加，旋轉角 = sin(dt * 0.002 * seed * 0.01) * angle * dir，
    //   opacity 隨 y 從 0 淡入（y < 0 時透明，y > 100px 後接近全不透明），
    //   飄出底部後重置到頂部，實現無限循環
    useEffect(() => {
        if (phase !== 'falling') return undefined;

        // 初始化 fall state
        fallState.current = makeFallState();

        let rafId;
        function loop(dt) {
            rafId = requestAnimationFrame(loop);
            const s = fallState.current;
            if (!s) return;

            // 垂直落下（速度受 fallSpeed prop 加成）
            s.y += s.v * fallSpeed;

            // 飄出底部 → 重置到頂部重新開始
            if (s.y > s.vh + size) {
                s.x = s.rangeStart + randomBetween(0, s.rangeWidth);
                s.y = -size;
            }

            // 旋轉角度：sin 曲線搖曳，與 canvas 參考完全對齊
            const rotDeg = s.dir * Math.sin(dt * 0.002 * s.seed * 0.01) * s.angle;

            // 水平搖曳（鐘擺式）：每片花瓣有獨立振幅、週期與初始相位
            const swayX = Math.sin(dt * s.swayFreq + s.swayPhase) * s.swayAmp;

            // 透明度：從螢幕頂部淡入
            const alpha = Math.max(0, Math.min(1, s.y * 0.01));

            setLeafStyle({
                opacity:   alpha,
                transform: `translate3d(${s.x + swayX}px, ${s.y}px, 0) scale(${s.scale}) rotate(${rotDeg}deg)`,
            });
        }

        rafId = requestAnimationFrame(loop);
        rafRef.current = rafId;

        return () => {
            cancelAnimationFrame(rafId);
            rafRef.current = null;
            fallState.current = null;
        };
    }, [phase, size, fallSpeed]);

    const handleClick = async () => {
        if (phase !== 'falling' || !rewardRef.current) return;
        const claimingReward = rewardRef.current;
        // 立即鎖住，避免同一組 key 被連續點擊觸發多次 claim
        rewardRef.current = null;
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        setPhase('collected');
        try {
            const res = await claimLotteryReward(claimingReward.lotteryId, claimingReward.key);
            if (!mountedRef.current) return;
            setEarnedPoints(res.point ?? 0);
            nextTimeRef.current = res.next_time;
        } catch (error) {
            console.error('Failed to claim lottery reward:', error);
            toast.error(error?.message || '領取失敗，請稍後再試');
            if (!mountedRef.current) return;
            setPhase('idle');
            // claim 失敗（例如 key 已被使用）時，重新問一次目前的實際狀態
            checkStatus();
        }
    };

    // 使用者關閉結果 Dialog 後才真正結束這一輪、排程下一次 status 檢查
    const handleDialogClose = () => {
        setPhase('idle');
        setEarnedPoints(null);
        pollTimerRef.current = setTimeout(checkStatus, msUntil(nextTimeRef.current) || 30000);
    };

    if (phase === 'idle') return null;

    return (
        <>
            {phase === 'falling' && (
                <div ref={containerRef} className={`falling-reward-root ${className}`} aria-hidden="true">
                    {/* rAF 逐幀計算位置：只用一個 fixed 錨點 div，transform 每幀更新 */}
                    <div
                        ref={hitRef}
                        className="falling-reward-fall"
                        style={{
                            willChange: 'transform, opacity',
                            position:  'fixed',
                            top:       0,
                            left:      0,
                            opacity:   leafStyle.opacity,
                            transform: leafStyle.transform,
                        }}
                    >
                        <button
                            type="button"
                            className="falling-reward-hit"
                            style={{ width: size, height: size }}
                            onClick={handleClick}
                            aria-label={resolvedType === 'lotus' ? '拾取蓮花' : '拾取菩提葉'}
                        >
                            <img
                                src={imageSrc}
                                alt=""
                                width={size}
                                height={size}
                                draggable={false}
                                style={{ display: 'block', pointerEvents: 'none' }}
                            />
                        </button>
                    </div>
                </div>
            )}

            {phase === 'collected' && earnedPoints !== null && (
                <RewardResultDialog
                    imageSrc={imageSrc}
                    points={earnedPoints}
                    label={resolvedType === 'lotus' ? '蓮花' : '菩提葉'}
                    onClose={handleDialogClose}
                />
            )}
        </>
    );
};

FallingRewardItem.propTypes = {
    type:            PropTypes.oneOf(['lotus', 'bodhi']),
    size:            PropTypes.number,
    spawnRangeRatio: PropTypes.number,
    fallSpeed:       PropTypes.number,
    windMagnitude:   PropTypes.number,
    className:       PropTypes.string,
};

export default FallingRewardItem;
